import { TranslationKey } from './../../../types/translation/TranslationKey';

import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../types/translation/Translator';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { Task, TaskStatus, TaskType } from '../../../types/task';
import { StockLocationContext } from '../../../context/StockLocationContext';
import { TaskContext } from '../../../context/TaskContext';
import CreateTaskModal from './Modals/CreateTaskModal';
import TaskIcon from '@mui/icons-material/Task';
import { useMutation } from '@apollo/client';
import { DownloadMultipleTextAsZip } from '../../../util/download.util';
import moment from 'moment-timezone';
import 'moment/min/locales';
import DeleteTaskModal from './Modals/DeleteTaskModal';
import { ReportMutations, ExportTaskResponse, ExportTaskVariables } from '../../../graphql/export.graphql';
import { CompanyContext } from '../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../context/CompanyRoleAssignmentContext';
import { testIds } from '../../../util/identifiers/identifiers.util';
import { CompanyRole } from '../../../types/company_role_assignment/companyRoleAssignment';
import TaskStatusLabel from '../Common/TaskStatusLabel';
import { StockLocationRoleAssignmentContext } from '../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import SearchBarWithFilter from '../../../VentoryUI/components/common/SearchBarWithFilter';
import { MenuItemProps } from '../../../VentoryUI/components/common/Menu/MenuItem';
import { ButtonProps, DeleteButtonTemplate } from '../../../VentoryUI/components/common/Button';
import { NewMenuItemTemplate } from '../../../VentoryUI/components/common/Menu/Templates/NewMenuItem';
import { SettingsMenuItemTemplate } from '../../../VentoryUI/components/common/Menu/Templates/SettingsMenuItem';
import Pane from '../../../VentoryUI/components/common/Pane';
import FlexPane from '../../../VentoryUI/components/common/FlexPane';
import Table from '../../components/Table';
import { TaskFilter } from '../../../VentoryUI/components/filters/TaskFilter';
import { UserContext } from '../../../context/UserContext';
import { BinStatusContext } from '../../../context/BinStatusContext';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { TagRelationContext } from '../../../context/TagRelationContext';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { BinContext } from '../../../context/BinContext';
import Provider from '../../../providers/common/Provider';
import TaskOverviewPane from './TaskOverviewPane';
import TaskSettingsScreen from './Settings/TaskSettingsScreen';
import TaskDetailPane from './Panes/TaskDetailPane';
import { Routing } from '../../../util/routes.util';
import dayjs from 'dayjs';

interface TaskScreenInputProps {}

export default function TaskScreen({}: TaskScreenInputProps) {
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const filterParam = params.get('filter') || '';

  const { tasks, tasksLoading } = useContext(TaskContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { bins } = useContext(BinContext);

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [items, setItems] = useState<Task[]>([...tasks.values()]);

  const [download] = useMutation<ExportTaskResponse, ExportTaskVariables>(ReportMutations.task, {
    onCompleted: res => handleFileDownload(res),
  });

  const handleFileDownload = (res: ExportTaskResponse) => {
    const file = res.generateTaskExport;
    DownloadMultipleTextAsZip(
      file.map(f => {
        return { name: f.fileName, text: f.content };
      }),
      `ventory_task_${new Date().toISOString()}.zip`,
    );
  };

  const handleDownload = (item: Task) => {
    if (!item) return;
    if (item.status === TaskStatus.complete || item.status === TaskStatus.processed)
      download({
        variables: {
          taskId: item.id,
          timezone: moment.tz.guess(),
          locale: (window.navigator as any)['userLanguage'] || window.navigator.language,
          companyId: item.companyId,
          stockLocationId: item.stockLocationId,
        },
      });
    return;
  };

  const headers = [
    {
      key: 'number',
      name: t(TranslationKey.number).singular.label,
      text: (item: Task) => {
        return item.number;
      },
    },
    {
      key: 'type',
      name: t(TranslationKey.type).singular.label,
      text: (item: Task) => {
        if (item.type === TaskType.cycle) {
          return t(TranslationKey.cycleCount).singular.label;
        } else if (item.type === TaskType.blind) {
          return t(TranslationKey.blindCount).singular.label;
        } else {
          return t(TranslationKey.recount).singular.label;
        }
      },
    },
    {
      key: 'stockLocation',
      name: t(TranslationKey.stockLocation).singular.label,
      text: (item: Task) => stockLocations.get(item.stockLocationId)?.name || 'Unknown Stock Location',
    },
    {
      key: 'createdOn',
      name: t(TranslationKey.createdAt).singular.label,
      text: (item: Task) => (item.createdAt ? new Date(item.createdAt).toLocaleDateString(locale) : ''),
      sortValue: (item: Task) => item.createdAt,
    },
    {
      key: 'dueDate',
      name: t(TranslationKey.dueDate).singular.label,
      text: (item: Task) => {
        return item.dueDate ? new Date(item.dueDate).toLocaleDateString(locale) : '';
      },
      sortValue: (item: Task) => (item.dueDate ? dayjs(item.dueDate) : null),
    },
    {
      key: 'completedOn',
      name: t(TranslationKey.completedOn).singular.label,
      text: (item: Task) => (item.completedOn ? new Date(item.completedOn).toLocaleDateString(locale) : ''),
      sortValue: (item: Task) => (item.completedOn ? dayjs(item.completedOn) : null),
    },
    {
      key: 'report',
      name: '',
      text: (item: Task) =>
        item.status === TaskStatus.complete || item.status === TaskStatus.processed ? (
          <div
            className='h-7'
            onClick={event => {
              event.stopPropagation();
              handleDownload(item);
            }}
          >
            <TaskIcon />
          </div>
        ) : (
          ''
        ),
    },
    {
      key: 'status',
      name: t(TranslationKey.status).singular.label,
      text: (item: Task) => <TaskStatusLabel task={item} />,
      sortValue: (item: Task) => item.status,
    },
  ];

  const menuItems: MenuItemProps[] = useMemo(() => {
    const shown: MenuItemProps[] = [];

    if (
      [...stockLocations.values()].filter(sl =>
        hasStockLocationRole(currentCompany.id, sl.id, StockLocationRole.STOCK_LOCATION_SUPERVISOR),
      )
    ) {
      shown.push(NewMenuItemTemplate(() => setOpenCreateModal(true)));
    }

    if (hasCompanyRole(currentCompany.id, CompanyRole.administrator)) {
      shown.push(SettingsMenuItemTemplate(() => navigate(Routing.tasks.settings.overview())));
    }

    return shown;
  }, [stockLocations]);

  const buttons: ButtonProps[] = useMemo(() => {
    const shown: ButtonProps[] = [];

    if (
      [...stockLocations.values()].filter(sl =>
        hasStockLocationRole(currentCompany.id, sl.id, StockLocationRole.STOCK_LOCATION_MANAGER),
      ) &&
      selected.size
    ) {
      shown.push(DeleteButtonTemplate(() => setOpenDeleteModal(true)));
    }

    return shown;
  }, [stockLocations, selected]);

  const allItems = useMemo(() => {
    return [...tasks.values()];
  }, [tasks]);

  const filter = new TaskFilter(
    stockLocations,
    companyUsers,
    binStatuses,
    productMasterData,
    tagRelations,
    customFields,
    bins,
    currentCompany,
    filterParam,
  );

  return (
    <TaskScreenProvider>
      <CreateTaskModal open={openCreateModal} setOpen={setOpenCreateModal} />
      <DeleteTaskModal
        open={openDeleteModal}
        setOpen={v => {
          setOpenDeleteModal(v);
          setSelected(new Set());
        }}
        taskIds={selected}
      />
      <Pane testId={testIds.taskScreen}>
        <FlexPane
          header={
            <SearchBarWithFilter
              loading={tasksLoading}
              items={allItems}
              setItems={setItems}
              filter={filter}
              placeholder={t(TranslationKey.filterTasks).singular.label}
              menuItems={menuItems}
              buttons={buttons}
            />
          }
          content={
            <Table
              title={t(TranslationKey.task).plural.label}
              loading={tasksLoading}
              totalItemCount={allItems.length}
              items={items}
              headers={headers}
              onClick={item => navigate(Routing.tasks.detail(item.id))}
              onSelected={items => setSelected(new Set(items))}
              filter={filter}
            />
          }
        />
      </Pane>
    </TaskScreenProvider>
  );
}

function TaskScreenProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider
      taskExportConfiguration
      stockLocationRoleAssignment
      taskSettings
      stockLocation
      binStatus
      productMasterData
      task
      bin
      lot
      product
      container
      user
      customField
      kit
    >
      <Routes>
        <Route path='*' element={<TaskOverviewPane />} />
        <Route path='detail/:id/*' element={<TaskDetailPane />} />
        <Route path='settings/*' element={<TaskSettingsScreen />} />
      </Routes>
    </Provider>
  );
}
